import {
    Directive,
    ElementRef,
    HostBinding,
    Input,
    OnChanges,
    Renderer2,
    SimpleChanges,
    TemplateRef
} from '@angular/core';

@Directive({
    selector: '[emptyCell]',
    standalone: true,
})
export class EmptyCellDirective implements OnChanges
{
    @Input() emptyCell: boolean;
    @Input() useZero: boolean;
    @Input() emptyCellText = '--';

    constructor(
        private _el: ElementRef,
        private _renderer: Renderer2,
    ) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes && changes.hasOwnProperty('emptyCell')) {
            if (this.emptyCell) {
                const emptyCell = this._renderer.createElement('span');
                emptyCell.textContent = this.useZero ? '0' : this.emptyCellText;
                emptyCell.classList.add('add-ashy-blue');

                this._el.nativeElement.textContent = '';
                this._renderer.appendChild(this._el.nativeElement, emptyCell);
            }
        }
    }
}

@Directive({
    selector: '[justifyRightCell]',
    standalone: true,
    host: { class: 'mat-column-justify-right' },
})
export class JustifyRightCellDirective
{
}

@Directive({
    selector: '[numberCell]',
    standalone: true,
    host: { class: 'mat-column-number' },
})
export class NumberCellDirective
{
}

@Directive({
    selector: '[appTable]',
    standalone: true,
    host: { class: 'app-table' },
})
export class AppTableDirective
{
    @Input() compact = false;

    @HostBinding('class.compact') get isCompact() {
        return this.compact;
    }
}

@Directive({
    selector: '[appTableRow]',
    standalone: true,
    host: { class: 'app-table-row' },
})
export class AppTableRowDirective
{
    @Input() headerRow = false;
    @Input() workingRow = false;
    @Input() selectableRow = false;
    @Input() deletedRow = false;
    @Input() firstRow = false;
    @Input() warningRow = false;
    @Input() disabledRow = false;

    @HostBinding('class.header-row') get addHeaderRowClass() {
        return this.headerRow;
    }
    @HostBinding('class.hover-row') get addHoverRowClass() {
        return this.selectableRow;
    }
    @HostBinding('class.first-row') get addFirstRowClass() {
        return this.firstRow;
    }
    @HostBinding('class.working-row') get addWorkingRowClass() {
        return this.workingRow;
    }
    @HostBinding('class.deleted-row') get addDeleteRowClass() {
        return this.deletedRow;
    }
    @HostBinding('class.warning-row') get addWarningRowClass() {
        return this.warningRow;
    }
    @HostBinding('class.disabled-row') get addDisabledRowClass() {
        return this.disabledRow;
    }
}

@Directive({
    selector: '[appTableActions]',
    standalone: true,
})
export class AppTableActionsDirective
{
    @Input() selectionCount = 0;

    constructor(public templateRef: TemplateRef<unknown>) {
    }
}

/**
 * Directive for projecting expanded row content into AppTableComponent.
 *
 * Usage:
 * ```html
 * <app-table [data]="items" ...>
 *     <ng-container matColumnDef="expand">
 *         <mat-header-cell *matHeaderCellDef></mat-header-cell>
 *         <mat-cell *matCellDef="let row">
 *             <button mat-icon-button (click)="dataTable.toggleRowExpansion(row); $event.stopPropagation()">
 *                 <mat-icon>{{ dataTable.isRowExpanded(row) ? 'expand_less' : 'expand_more' }}</mat-icon>
 *             </button>
 *         </mat-cell>
 *     </ng-container>
 *
 *     <!-- Other columns... -->
 *
 *     <ng-template appTableExpandedRow let-row>
 *         <div class="expanded-content">
 *             <!-- Expanded content here -->
 *         </div>
 *     </ng-template>
 * </app-table>
 * ```
 */
@Directive({
    selector: '[appTableExpandedRow]',
    standalone: true,
})
export class AppTableExpandedRowDirective<T = unknown> {
    constructor(public templateRef: TemplateRef<{ $implicit: T }>) {}
}
